import { useRef, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useLazyQuery } from '@apollo/client';
import {
  Box,
  chakra,
  Collapse,
  FormControl,
  FormErrorMessage,
  InputGroup,
  InputRightElement,
  useBreakpointValue,
  VStack,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { AnimatePresence, motion } from 'framer-motion';

import asBentoPage from 'common/bento/hoc/asBentoPage';
import * as Form from 'components/_core/form';
import IconButton from 'components/_core/IconButton';
import SuspenseQuery from 'components/SuspenseQuery';
import OBBackButton from 'features/Onboarding/components/OBBackButton';
import OBNavigation from 'features/Onboarding/components/OBNavigation/OBNavigation';

import Header from '../../../../components/Header';
import { ExternalCompanyProfileInfo } from '../../types';
import { hybridSiretInput, isSirenLike } from '../../utils/hybridSiretUtils';
import NoResults from './components/NoResults';
import Results from './components/Results';
import SecondaryOptions from './components/SecondaryOptions';
import { ExternalCompanyLookupDocument } from './graphql/queries/externalCompanyLookup.gql';
import locales from './locales';
import { searchSchema } from './schema';

interface SearchProps {
  onSkip: () => void;
  onGoCapitalDeposit: () => void;
  onSelectCompany: (company: ExternalCompanyProfileInfo) => void;
}

const Search = asBentoPage<SearchProps>(
  ({ Layout, onGoCapitalDeposit, onPrev, onSelectCompany, onSkip }) => {
    const {
      control,
      formState: { errors },
      handleSubmit,
      reset,
    } = useForm({
      defaultValues: { searchInput: '' },
      resolver: zodResolver(searchSchema),
      reValidateMode: 'onSubmit',
    });

    /**
     * Note that the `isValid` RHF prop doesn't work with `onSubmit` validate mode.
     * Therefore we derive it from the presence or absence of errors.
     */
    const isInvalid = Boolean(errors.searchInput);

    const { searchInput } = useWatch({ control });

    const [isFocused, setIsFocused] = useState(false);

    const [fetchExternalCompanies, query] = useLazyQuery(
      ExternalCompanyLookupDocument,
    );

    const isMobile = useBreakpointValue({ base: true, md: false });
    const isHeaderCollapsed = isMobile && (searchInput !== '' || isFocused);

    const inputRef = useRef<HTMLInputElement | null>(null);

    return (
      <Layout
        data-testid="business-search"
        header={<OBBackButton onClick={onPrev} />}
        nav={<OBNavigation onPrev={onPrev} />}
      >
        <Collapse in={!isHeaderCollapsed}>
          <Header title={locales.title} />
        </Collapse>

        <chakra.form
          marginBottom="space-4"
          onSubmit={handleSubmit((payload) => {
            inputRef.current?.blur();

            return fetchExternalCompanies({
              variables: payload,
            });
          })}
        >
          <FormControl
            alignItems="stretch"
            as={VStack}
            isInvalid={isInvalid}
            spacing="space-4"
          >
            <Controller
              control={control}
              name="searchInput"
              render={({
                field: { onBlur, onChange, ref, value, ...fieldRest },
              }) => (
                <InputGroup>
                  <Form.Input
                    {...fieldRest}
                    data-testid="business-search-bar"
                    inputMode="search"
                    onBlur={() => {
                      setIsFocused(false);
                      onBlur();
                    }}
                    onChange={(e) =>
                      onChange(hybridSiretInput.unmask(e.target.value))
                    }
                    onFocus={() => setIsFocused(true)}
                    placeholder={locales.inputPlaceholder}
                    ref={(element) => {
                      inputRef.current = element;
                      ref(element);
                    }}
                    required
                    value={hybridSiretInput.mask(value)}
                  />

                  <InputRightElement
                    height="full"
                    paddingRight="space-4"
                    width="auto"
                  >
                    {searchInput && !query.loading ? (
                      <IconButton
                        animate={{ opacity: 1, transition: { duration: 0.1 } }}
                        aria-label={locales.clearButton}
                        as={motion.button}
                        borderBottom="none"
                        fontSize="font-13"
                        icon="cross"
                        initial={{ opacity: 0 }}
                        onClick={() => reset()}
                        size="sm"
                        variant="inline-secondary"
                      />
                    ) : null}

                    <IconButton
                      aria-label={locales.searchButton}
                      disabled={
                        searchInput === '' ||
                        searchInput === query.variables?.searchInput ||
                        query.loading
                      }
                      fontSize="font-20"
                      icon="search"
                      isLoading={query.loading}
                      size="sm"
                      type="submit"
                      variant="inline-primary"
                    />
                  </InputRightElement>
                </InputGroup>
              )}
            />

            <Collapse in={isInvalid}>
              {errors.searchInput ? (
                <FormErrorMessage marginLeft="space-24">
                  {errors.searchInput.message}
                </FormErrorMessage>
              ) : null}
            </Collapse>
          </FormControl>
        </chakra.form>

        <Box>
          {query.called && searchInput === query.variables?.searchInput ? (
            <SuspenseQuery loader={<>{null}</>} query={query}>
              {({ externalCompanyLookup }) => (
                <motion.div
                  animate={{ opacity: 1, y: 0 }}
                  data-testid="results-list"
                  initial={{ opacity: 0, y: 10 }}
                  key={query.variables?.searchInput}
                >
                  {externalCompanyLookup.length > 0 ? (
                    <Results
                      companies={externalCompanyLookup}
                      onSelectCompany={onSelectCompany}
                      onSkip={onSkip}
                    />
                  ) : (
                    <NoResults
                      hasBeenSearchedBySIREN={isSirenLike(
                        query.variables?.searchInput ?? '',
                      )}
                      onSkip={onSkip}
                    />
                  )}
                </motion.div>
              )}
            </SuspenseQuery>
          ) : (
            <AnimatePresence>
              {!isHeaderCollapsed ? (
                <motion.div
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  initial={{ opacity: 0, y: -8 }}
                >
                  <SecondaryOptions
                    onGoCapitalDeposit={onGoCapitalDeposit}
                    onSkip={onSkip}
                  />
                </motion.div>
              ) : null}
            </AnimatePresence>
          )}
        </Box>
      </Layout>
    );
  },
);

export default Search;
